import React, {
  useState,
  useEffect,
  useCallback,
} from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  checkPreviousValuesExist,
  useApplyPreviousValues,
} from '../utils/previous-values-manager';
import BatchHistoryModal from './BatchHistoryModal';
import DryUpLoadModal from './DryUpLoadModal';
import { useStores } from '../../../hooks/use-stores';

interface BatchFormExtraToolsProps {
  currentValues: any;
  setValues: (values: any) => void;
  setInitialValues?: (values: any) => void;
  isSubmitting?: boolean;
}

const BatchFormExtraTools: React.FC<BatchFormExtraToolsProps> = ({
  currentValues,
  setValues,
  setInitialValues,
  isSubmitting,
}) => {
  const { t } = useTranslation();
  const { settingsStore } = useStores();
  const applyPreviousValues = useApplyPreviousValues();
  const [hasPreviousValues, setHasPreviousValues] = useState<boolean>(false);
  const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false);
  const [showDryUpModal, setShowDryUpModal] = useState<boolean>(false);

  useEffect(() => {
    const checkForPreviousValues = async () => {
      const exists = await checkPreviousValuesExist(settingsStore);
      setHasPreviousValues(exists);
    };

    checkForPreviousValues();
  }, [settingsStore]);

  const handleCopyPrevious = useCallback(async () => {
    const calculatedValues = await applyPreviousValues(
      currentValues,
      setValues,
    );

    if (calculatedValues && setInitialValues) {
      setInitialValues(calculatedValues);
    }
  }, [
    currentValues,
    setValues,
    setInitialValues,
    applyPreviousValues,
  ]);

  const devMode = process.env.NODE_ENV === 'development';

  return (
    <>
      <div className="dev-tools">
        <Button
          bsSize="xsmall"
          onClick={() => setShowDryUpModal(true)}
          disabled={isSubmitting}
          style={{ marginLeft: '8px' }}
          bsStyle="warning"
          title={t('Load materials without water')}
          data-e2e-id="btn-dry-up-load"
        >
          <i className="fa fa-tint-slash" /> {t('Dry Up Load')}
        </Button>
        <Button
          bsSize="xsmall"
          onClick={() => setShowHistoryModal(true)}
          disabled={isSubmitting}
          style={{ marginLeft: '8px' }}
          title={t('Select from recent batches')}
          data-e2e-id="btn-batch-history"
        >
          <i className="fa fa-history" /> {t('Batch History')}
        </Button>
        {devMode && (
          <>
            <Button
              bsSize="xsmall"
              onClick={handleCopyPrevious}
              style={{ marginLeft: '8px' }}
              disabled={isSubmitting || !hasPreviousValues}
              title={hasPreviousValues ? t('Load values from previous batch') : t('No previous values saved yet')}
            >
              <i className="fa fa-copy" /> {t('Copy Previous')}
            </Button>
          </>
        )}
      </div>
      <BatchHistoryModal
        show={showHistoryModal}
        onHide={() => setShowHistoryModal(false)}
        currentValues={currentValues}
        setValues={setValues}
        setInitialValues={setInitialValues}
      />
      <DryUpLoadModal
        show={showDryUpModal}
        onHide={() => setShowDryUpModal(false)}
        currentValues={currentValues}
        setValues={setValues}
        setInitialValues={setInitialValues}
      />
    </>
  );
};

export default BatchFormExtraTools;
